export const ALL_SPECIALITIES_LABEL = "All Specialities";

export const NAME_PATTERN = /^[a-zA-Z0-9\s/\\&\-(),.']+$/;
export const NAME_MAX = 255;
export const VALUE_MAX = 200;

export const toValueList = (values) =>
  (Array.isArray(values) ? values : [])
    .map((item) => (typeof item === "string" ? item : item?.name))
    .filter(Boolean);

export const cleanValues = (values) => {
  const seen = new Set();
  const cleaned = [];
  const duplicates = [];

  toValueList(values).forEach((raw) => {
    const value = String(raw).trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) {
      duplicates.push(value);
      return;
    }
    seen.add(key);
    cleaned.push(value);
  });

  return { values: cleaned, duplicates };
};

export const validateFindingForm = ({
  name,
  values = [],
  speciality = [],
  allSpecialities = false,
}) => {
  const trimmed = String(name ?? "").trim();

  if (!trimmed) return "Finding is required";
  if (trimmed.length < 2) return "Use at least 2 characters";
  if (trimmed.length > NAME_MAX) return `Use at most ${NAME_MAX} characters`;
  if (!NAME_PATTERN.test(trimmed)) {
    return "Only letters, numbers and - / & ( ) , . ' are allowed";
  }
  if (toValueList(values).some((value) => String(value).trim().length > VALUE_MAX)) {
    return `Each value must be at most ${VALUE_MAX} characters`;
  }
  if (allSpecialities && speciality.length > 0) {
    return `"${ALL_SPECIALITIES_LABEL}" cannot be combined with specific specialities`;
  }
  if (!allSpecialities && speciality.length === 0) {
    return `Select at least one speciality, or choose "${ALL_SPECIALITIES_LABEL}"`;
  }
  return "";
};

export const buildFindingPayload = ({
  name,
  values = [],
  speciality = [],
  allSpecialities = false,
}) => ({
  name: String(name ?? "").trim(),
  values: cleanValues(values).values.map((value) => ({ name: value })),
  speciality: allSpecialities
    ? []
    : speciality.map((item) => (typeof item === "string" ? item : item?._id)),
  allSpecialities: Boolean(allSpecialities),
});

/** An existing finding with no speciality is an "All Specialities" one. */
export const specialityStateOf = (finding) => {
  const selected = Array.isArray(finding?.speciality)
    ? finding.speciality.filter((item) => item && typeof item === "object")
    : [];
  return {
    speciality: selected,
    allSpecialities: Boolean(finding?._id) && selected.length === 0,
  };
};

export const importFailures = (data = {}) => {
  const failed = (Array.isArray(data?.results) ? data.results : [])
    .filter((item) => item?.status === "failed")
    .map((item) => ({
      row: Number(item.row) || null,
      name: String(item.name ?? ""),
      reason: String(item.message ?? "Could not import this row"),
    }));

  const unreadable = (Array.isArray(data?.skippedRows) ? data.skippedRows : []).map(
    (item) => ({
      row: Number(item?.row) || null,
      name: "",
      reason: String(item?.reason ?? "Could not read this row"),
    })
  );

  return [...failed, ...unreadable].sort(
    (a, b) => (a.row ?? Infinity) - (b.row ?? Infinity)
  );
};

export const importSummary = (data = {}) => {
  const {
    created = 0,
    updated = 0,
    skipped = 0,
    failed = 0,
    specialityIgnored = 0,
  } = data || {};

  return {
    level: failed > 0 ? "warning" : "success",
    message: `Import complete — ${created} created, ${updated} updated, ${skipped} skipped, ${failed} failed.`,
    specialityNote:
      specialityIgnored > 0
        ? `${specialityIgnored} existing finding(s) kept their current speciality mapping. Imported speciality was ignored — change it from the Edit screen.`
        : "",
  };
};
